/**
 * Basic CRUD operations for routes
 */

import { doc, getDoc, writeBatch, collection, getDocs, deleteDoc, query, where } from 'firebase/firestore'
import { db, auth } from '../../../lib/firebase'
import { RouteData, POI, Photo } from '@cya-routes/shared'
import { UnifiedRoute } from './types'
import { deserializeFromFirebase } from '../utils'
import { decompressRouteFields } from '../../../utils/compression'
import { cleanupRoutePhotos } from './routePhotos'
import { getStatusFromType } from './compatibility'

/**
 * Load a route by ID
 * @param routeId - The route ID
 * @param lightweight - If true, skip loading POIs and photos
 * @returns The route data or null if not found
 */
export const loadRoute = async (routeId: string, lightweight = false): Promise<UnifiedRoute | null> => {
  try {
    const routeBasePath = doc(db, 'routes', routeId)
    const routeDoc = await getDoc(routeBasePath)
    
    if (!routeDoc.exists()) {
      console.log('Route not found:', routeId)
      return null
    }
    
    const rawMetadata = routeDoc.data()
    const metadata = deserializeFromFirebase(rawMetadata)
    
    // Handle old routes that have type but no status
    if (!metadata.status && metadata.type) {
      metadata.status = getStatusFromType(metadata.type)
      console.log(`🔄 Migrated old route: type "${metadata.type}" → status "${metadata.status}"`)
    }
    
    console.log(`📂 Loading route ${routeId} - type: ${metadata.type}, status: ${metadata.status}`)
    console.log('📝 Loaded metadata with description:', {
      name: metadata.name,
      description: metadata.description,
      masterRouteDescription: metadata.masterRouteDescription,
      hasDescription: !!metadata.description,
      attribution: metadata.attribution,
      descriptionLength: metadata.description?.length || 0
    })
    
    // Check if route needs photo cleanup (has index-based photo documents)
    const photosSnapshot = await getDocs(collection(routeBasePath, 'photos'))
    const hasIndexBasedPhotos = photosSnapshot.docs.some(doc => /^\d+$/.test(doc.id))
    if (hasIndexBasedPhotos) {
      console.log('🧹 Route has index-based photos, performing cleanup...')
      await cleanupRoutePhotos(routeId)
    }
    
    // Load segments
    const segmentsSnapshot = await getDocs(collection(routeBasePath, 'segments'))
    const routes = segmentsSnapshot.docs
      .sort((a, b) => {
        // First try to sort by order field (new structure)
        const aOrder = a.data().order
        const bOrder = b.data().order
        if (aOrder !== undefined && bOrder !== undefined) {
          return aOrder - bOrder
        }
        // Fallback to sorting by document ID for legacy data
        return Number(a.id) - Number(b.id)
      })
      .map((doc, index) => {
        const rawData = doc.data();
        const data = deserializeFromFirebase(rawData) as RouteData;
        // Decompress if needed
        const route = decompressRouteFields(data);
        
        // Preserve excludeFromMaster flag from raw data
        if (rawData.excludeFromMaster !== undefined) {
          route.excludeFromMaster = rawData.excludeFromMaster;
        }
        
        // Debug logging for segment descriptions
        if (index === 0) {
          console.log('📄 First segment description:', route.description?.substring(0, 100) || 'NO DESCRIPTION');
        }
        
        // Ensure each segment has a unique ID (fix for legacy data)
        if (!route.id || route.id.startsWith('route_')) {
          // Generate unique ID for segments that don't have one or are using the route ID
          route.id = `segment_${index}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        }
        
        // Ensure segmentIndex is set
        if (route.segmentIndex === undefined) {
          route.segmentIndex = index;
        }
        
        return route;
      })
    console.log(`📍 Loaded ${segmentsSnapshot.docs.length} segments`)
    
    
    // Load POIs and photos only if not lightweight
    let pois: POI[] = []
    let photos: Photo[] = []
    
    if (!lightweight) {
      // Load POIs
      const poisSnapshot = await getDocs(collection(routeBasePath, 'pois'))
      pois = poisSnapshot.docs
        .sort((a, b) => Number(a.id) - Number(b.id))
        .map(doc => deserializeFromFirebase(doc.data()) as POI)
      console.log(`🎯 Loaded ${poisSnapshot.docs.length} POIs`)
      
      // Load photos
      console.log(`📸 Attempting to load photos from: routes/${routeId}/photos`)
      const photosSnapshot = await getDocs(collection(routeBasePath, 'photos'))
      console.log(`📸 Photos snapshot size: ${photosSnapshot.size}, empty: ${photosSnapshot.empty}`)
      // Create a map to deduplicate photos by ID
      const photoMap = new Map<string, Photo>()
      
      photosSnapshot.docs.forEach(doc => {
        const photo = deserializeFromFirebase(doc.data())
        // Handle Firebase Timestamp for takenAt
        let takenAt: Date | undefined
        if (photo.takenAt) {
          if (typeof photo.takenAt.toDate === 'function') {
            // Firebase Timestamp object
            takenAt = photo.takenAt.toDate()
          } else if (photo.takenAt instanceof Date) {
            // Already a Date object
            takenAt = photo.takenAt
          } else if (typeof photo.takenAt === 'object' && 'seconds' in photo.takenAt) {
            // Timestamp with seconds property
            takenAt = new Date(photo.takenAt.seconds * 1000)
          } else {
            // Try to parse as string or number
            takenAt = new Date(photo.takenAt)
            if (isNaN(takenAt.getTime())) {
              takenAt = undefined
            }
          }
        }
        
        const photoData = {
          ...photo,
          takenAt
        } as Photo
        
        // Only add if we haven't seen this photo ID before (deduplication)
        if (!photoMap.has(photoData.id)) {
          photoMap.set(photoData.id, photoData)
        } else {
          console.warn(`⚠️ Duplicate photo found in Firebase: ${photoData.id}, skipping`)
        }
      })
      
      // Convert map to array and sort by upload date
      photos = Array.from(photoMap.values()).sort((a, b) => {
        const dateA = a.uploadedAt ? new Date(a.uploadedAt).getTime() : 0
        const dateB = b.uploadedAt ? new Date(b.uploadedAt).getTime() : 0
        return dateA - dateB
      })
      console.log(`📸 Loaded ${photosSnapshot.docs.length} photos`)
      if (photos.length > 0) {
        console.log(`📸 First photo details:`, {
          id: photos[0].id,
          url: photos[0].url,
          thumbnailUrl: photos[0].thumbnailUrl,
          fileName: photos[0].fileName
        })
      }
    } else {
      console.log(`🚀 Lightweight mode: Skipping POIs and photos for route ${routeId}`)
    }
    
    // Ensure date fields are properly set
    const createdAt = metadata.createdAt instanceof Date ? metadata.createdAt : new Date()
    const updatedAt = metadata.updatedAt instanceof Date ? metadata.updatedAt : new Date()
    const lastAutosaveAt = metadata.lastAutosaveAt instanceof Date ? metadata.lastAutosaveAt : undefined
    
    return {
      ...metadata,
      id: routeId,
      coordinates: metadata.coordinates ? deserializeFromFirebase(metadata.coordinates) : [],
      routes,
      pois,
      photos,
      createdAt,
      updatedAt,
      lastAutosaveAt,
      submission: metadata.submission ? {
        ...metadata.submission,
        submittedAt: metadata.submission.submittedAt instanceof Date ? metadata.submission.submittedAt : new Date(),
        reviewedAt: metadata.submission.reviewedAt instanceof Date ? metadata.submission.reviewedAt : undefined
      } : undefined
    } as UnifiedRoute
  } catch (error) {
    console.error('Failed to load route:', error)
    return null
  }
}

/**
 * Delete a route and all its subcollections
 * @param routeId - The route ID to delete
 * @returns True if successful, false otherwise
 */
export const deleteUnifiedRoute = async (routeId: string): Promise<boolean> => {
  try {
    const currentUser = auth.currentUser
    if (!currentUser) return false
    
    // Verify ownership
    const routeDoc = await getDoc(doc(db, 'routes', routeId))
    if (!routeDoc.exists() || routeDoc.data().ownerId !== currentUser.uid) {
      return false
    }
    
    const routeBasePath = doc(db, 'routes', routeId)
    
    // Delete all subcollections
    const [segmentsSnapshot, poisSnapshot, photosSnapshot] = await Promise.all([
      getDocs(collection(routeBasePath, 'segments')),
      getDocs(collection(routeBasePath, 'pois')),
      getDocs(collection(routeBasePath, 'photos'))
    ])
    
    // Count total operations
    const totalOps = segmentsSnapshot.docs.length + poisSnapshot.docs.length + 
                     photosSnapshot.docs.length + 1 // +1 for metadata doc
    
    if (totalOps <= 500) {
      // Can do in one batch
      const batch = writeBatch(db)
      segmentsSnapshot.docs.forEach(doc => batch.delete(doc.ref))
      poisSnapshot.docs.forEach(doc => batch.delete(doc.ref))
      photosSnapshot.docs.forEach(doc => batch.delete(doc.ref))
      batch.delete(routeBasePath)
      await batch.commit()
    } else {
      // Need multiple batches
      let batch = writeBatch(db)
      let operationCount = 0
      
      // Delete segments
      for (const doc of segmentsSnapshot.docs) {
        batch.delete(doc.ref)
        operationCount++
        
        if (operationCount >= 400) {
          await batch.commit()
          batch = writeBatch(db)
          operationCount = 0
        }
      }
      
      // Delete POIs
      for (const doc of poisSnapshot.docs) {
        batch.delete(doc.ref)
        operationCount++
        
        if (operationCount >= 400) {
          await batch.commit()
          batch = writeBatch(db)
          operationCount = 0
        }
      }
      
      // Delete photos
      for (const doc of photosSnapshot.docs) {
        batch.delete(doc.ref)
        operationCount++
        
        if (operationCount >= 400) {
          await batch.commit()
          batch = writeBatch(db)
          operationCount = 0
        }
      }
      
      // Delete metadata document
      batch.delete(routeBasePath)
      await batch.commit()
    }
    
    console.log('✅ Route deleted successfully:', routeId)
    return true
  } catch (error) {
    console.error('Failed to delete route:', error)
    return false
  }
}

/**
 * Load a route by its hierarchical slug
 * @param slug - The hierarchical slug (e.g., "australia/victoria/grampians/great-ocean-road")
 * @returns The route data or null if not found
 */
export const loadRouteBySlug = async (slug: string): Promise<any | null> => {
  try {
    console.log('🔍 Loading route by slug:', slug)
    
    // Query for routes with matching slug
    // Routes can be type='published' with various statuses (submitted, published, etc.)
    const routesRef = collection(db, 'routes')
    const q = query(routesRef, where('slug', '==', slug), where('type', '==', 'published'))
    
    const querySnapshot = await getDocs(q)
    
    if (querySnapshot.empty) {
      console.log('No route found with slug:', slug)
      return null
    }
    
    // Should only be one route with a given slug
    const routeDoc = querySnapshot.docs[0]
    const routeId = routeDoc.id
    
    console.log('Found route with slug:', { slug, routeId })
    
    // Load the full route data
    return await loadRoute(routeId)
  } catch (error) {
    console.error('Failed to load route by slug:', error)
    return null
  }
}